import logger from '@/utils/logger';
import React, { useReducer, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Lock } from 'lucide-react';
import { useAuth } from '../components/auth/AuthProvider';

// FIX: State machine avec useReducer (plus stable que multiples useState)
type State =
 | { status: 'verifying' }
 | { status: 'valid'; email: string; fullName: string }
 | { status: 'invalid'; error: string }
 | { status: 'submitting'; email: string; fullName: string }
 | { status: 'error'; email: string; fullName: string; error: string };

type Action =
 | { type: 'VERIFICATION_SUCCESS'; email: string; fullName: string }
 | { type: 'VERIFICATION_FAILED'; error: string }
 | { type: 'SUBMIT_START' }
 | { type: 'SUBMIT_ERROR'; error: string }
 | { type: 'CLEAR_ERROR' };

function reducer(state: State, action: Action): State {
 switch (action.type) {
 case 'VERIFICATION_SUCCESS':
 return { status: 'valid', email: action.email, fullName: action.fullName };
 case 'VERIFICATION_FAILED':
 return { status: 'invalid', error: action.error };
 case 'SUBMIT_START':
 if (state.status === 'valid' || state.status === 'error') {
 return { status: 'submitting', email: state.email, fullName: state.fullName };
 }
 return state;
 case 'SUBMIT_ERROR':
 if (state.status === 'submitting') {
 return { status: 'error', email: state.email, fullName: state.fullName, error: action.error };
 }
 return state;
 case 'CLEAR_ERROR':
 if (state.status === 'error') {
 return { status: 'valid', email: state.email, fullName: state.fullName };
 }
 return state;
 default:
 return state;
 }
}

export default function BetaSignup() {
 const [searchParams] = useSearchParams();
 const navigate = useNavigate();
 const { signUp } = useAuth();

 const [state, dispatch] = useReducer(reducer, { status: 'verifying' });
 const [password, setPassword] = React.useState('');
 const [confirmPassword, setConfirmPassword] = React.useState('');
 const [token, setToken] = React.useState('');

 // Vérifier le token au chargement (avec cleanup)
 useEffect(() => {
 const tokenParam = searchParams.get('token');
 
 if (!tokenParam) {
 dispatch({ type: 'VERIFICATION_FAILED', error: 'Token d\'invitation manquant' });
 return;
 }

 setToken(tokenParam);

 let cancelled = false;

 const verifyToken = async () => {
 try {
 const backendUrl = window.location.hostname === 'localhost'
 ? 'http://localhost:3000'
 : 'https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud';
 
 logger.log(' Vérification token sur:', backendUrl);
 
 const response = await fetch(`${backendUrl}/api/waitlist/verify-token/${tokenParam}`);
 const data = await response.json();

 if (cancelled) return; // Cleanup: ne pas update state si unmounted

 if (data.valid) {
 dispatch({ type: 'VERIFICATION_SUCCESS', email: data.email, fullName: data.name });
 logger.log(' Token valide:', data.email);
 } else {
 dispatch({ type: 'VERIFICATION_FAILED', error: data.error || 'Token invalide ou expiré' });
 }
 } catch (error: any) {
 if (cancelled) return;
 logger.error(' Erreur vérification token:', error);
 dispatch({ type: 'VERIFICATION_FAILED', error: 'Erreur de vérification. Veuillez réessayer.' });
 }
 };

 verifyToken();

 return () => {
 cancelled = true; // Cleanup
 };
 }, [searchParams]);

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 
 if (state.status !== 'valid' && state.status !== 'error') {
 return;
 }

 // Validation
 if (password.length < 8) {
 dispatch({ type: 'SUBMIT_ERROR', error: 'Le mot de passe doit contenir au moins 8 caractères' });
 return;
 }

 if (password !== confirmPassword) {
 dispatch({ type: 'SUBMIT_ERROR', error: 'Les mots de passe ne correspondent pas' });
 return;
 }

 dispatch({ type: 'SUBMIT_START' });

 try {
 const { data: signUpData, error: signUpError } = await signUp(state.email, password, {
 full_name: state.fullName,
 is_beta_tester: true,
 invitation_token: token
 });

 if (signUpError) {
 if (signUpError.message === 'User already registered') {
 logger.warn(' Compte existe déjà, tentative de connexion...');
 dispatch({ type: 'SUBMIT_ERROR', error: 'Ce compte existe déjà. Redirection vers la connexion...' });
 setTimeout(() => navigate('/login'), 2000);
 return;
 }
 throw signUpError;
 }

 logger.log(' Compte beta créé avec succès:', state.email);

 const studioUrl = window.location.hostname === 'localhost' ? '/studio' : 'https://clairify.fr/studio';
 if (studioUrl.startsWith('http')) {
 window.location.href = studioUrl;
 } else {
 navigate(studioUrl);
 }
 } catch (error: any) {
 logger.error(' Erreur création compte:', error);
 
 const errorMessages: Record<string, string> = {
 'User already registered': 'Un compte existe déjà avec cet email',
 'Password should be at least 8 characters': 'Le mot de passe doit contenir au moins 8 caractères',
 'Invalid email': 'Format d\'email invalide',
 };

 dispatch({ type: 'SUBMIT_ERROR', error: errorMessages[error.message] || error.message || 'Erreur lors de la création du compte' });
 }
 };

 // État de chargement
 if (state.status === 'verifying') {
 return (
 <div className="min-h-screen bg-theme-base flex items-center justify-center p-4">
 <div className="text-center">
 <Loader2 className="w-12 h-12 text-accent-primary animate-spin mx-auto mb-4" />
 <p className="text-theme-secondary">Vérification de votre invitation...</p>
 </div>
 </div>
 );
 }

 // Token invalide
 if (state.status === 'invalid') {
 return (
 <div className="min-h-screen bg-theme-base flex items-center justify-center p-4">
 <div className="w-full max-w-md">
 <div className="bg-theme-surface rounded-2xl shadow-2xl p-8 border border-theme-border text-center">
 <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
 <XCircle className="w-8 h-8 text-red-500" />
 </div>
 <h2 className="text-2xl font-bold text-theme-primary mb-4">
 Invitation invalide
 </h2>
 <p className="text-theme-secondary mb-6">
 {state.error}
 </p>
 <Link
 to="/waitlist"
 className="inline-block bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-semibold py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-200"
 >
 Demander un accès
 </Link>
 </div>
 </div>
 </div>
 );
 }

 // Formulaire de création de compte (valid ou submitting ou error)
 const isSubmitting = state.status === 'submitting';
 const formError = state.status === 'error' ? state.error : null;

 return (
 <div className="min-h-screen bg-theme-base flex items-center justify-center p-4">
 <div className="w-full max-w-md">
 <div className="bg-theme-surface rounded-2xl shadow-2xl p-8 border border-theme-border">
 {/* Header */}
 <div className="text-center mb-6">
 <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
 <CheckCircle className="w-8 h-8 text-green-500" />
 </div>
 <h2 className="text-2xl font-bold text-theme-primary mb-2">
 Bienvenue dans la Beta !
 </h2>
 <p className="text-theme-secondary text-sm">
 Créez votre compte pour accéder à Clairify
 </p>
 </div>

 {/* Info beta tester */}
 <div className="bg-accent-primary/10 border border-accent-primary/20 rounded-lg p-4 mb-6">
 <p className="text-sm text-theme-secondary">
 <strong className="text-accent-primary"> Cadeau de bienvenue</strong><br />
 500 000 tokens offerts pour tester Clairify
 </p>
 </div>

 {/* Formulaire */}
 <form onSubmit={handleSubmit} className="space-y-4">
 {/* Nom (pré-rempli, lecture seule) */}
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 Nom complet
 </label>
 <input
 type="text"
 value={state.fullName}
 readOnly
 className="w-full px-4 py-3 bg-theme-base/50 border border-theme-border rounded-lg text-theme-primary cursor-not-allowed"
 style={{ color: '#1f2937', opacity: 0.7 }}
 />
 </div>

 {/* Email (pré-rempli, lecture seule) */}
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 Email
 </label>
 <input
 type="email"
 value={state.email}
 readOnly
 className="w-full px-4 py-3 bg-theme-base/50 border border-theme-border rounded-lg text-theme-primary cursor-not-allowed"
 style={{ color: '#1f2937', opacity: 0.7 }}
 />
 </div>

 {/* Mot de passe */}
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 <Lock className="w-4 h-4 inline mr-2" />
 Mot de passe
 </label>
 <input
 type="password"
 value={password}
 onChange={(e) => {
 setPassword(e.target.value);
 if (formError) dispatch({ type: 'CLEAR_ERROR' });
 }}
 className="w-full px-4 py-3 bg-theme-base border border-theme-border rounded-lg text-theme-primary placeholder-theme-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all"
 placeholder="Minimum 8 caractères"
 required
 minLength={8}
 disabled={isSubmitting}
 />
 </div>

 {/* Confirmation mot de passe */}
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 <Lock className="w-4 h-4 inline mr-2" />
 Confirmer le mot de passe
 </label>
 <input
 type="password"
 value={confirmPassword}
 onChange={(e) => {
 setConfirmPassword(e.target.value);
 if (formError) dispatch({ type: 'CLEAR_ERROR' });
 }}
 className="w-full px-4 py-3 bg-theme-base border border-theme-border rounded-lg text-theme-primary placeholder-theme-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all"
 placeholder="Confirmez votre mot de passe"
 required
 minLength={8}
 disabled={isSubmitting}
 />
 </div>

 {/* Message d'erreur */}
 {formError && (
 <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
 <p className="text-sm text-red-500">{formError}</p>
 </div>
 )}

 {/* Bouton de soumission */}
 <button
 type="submit"
 disabled={isSubmitting}
 className="w-full bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-semibold py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
 >
 {isSubmitting ? (
 <>
 <Loader2 className="w-5 h-5 animate-spin" />
 Création en cours...
 </>
 ) : (
 'Créer mon compte →'
 )}
 </button>
 </form>

 {/* Footer */}
 <div className="mt-6 text-center">
 <p className="text-xs text-theme-tertiary">
 En créant un compte, vous acceptez nos conditions d'utilisation
 </p>
 </div>
 </div>
 </div>
 </div>
 );
}

